/**
 * Google Autocomplete Hook - V2 Implementation
 * Compatible with V1 interface while using V2 core architecture
 */

import { useCallback, useEffect, useRef, useState } from 'react';

// Type declarations for Google Maps API when types are not available
declare global {
    interface Window {
        google?: {
            maps?: {
                places?: {
                    AutocompleteService: new () => any;
                    PlacesService: new (map: any) => any;
                    PlacesServiceStatus: {
                        OK: string;
                        ZERO_RESULTS: string;
                    };
                };
                Map: new (element: HTMLElement) => any;
            };
        };
    }
}

// Types for Google Places API (compatible with V1)
export interface GooglePrediction {
    place_id: string;
    description: string;
    structured_formatting?: {
        main_text: string;
        secondary_text: string;
    };
}

export interface GooglePlaceDetails {
    place_id: string;
    formatted_address: string;
    address_components: {
        long_name: string;
        short_name: string;
        types: string[];
    }[];
    geometry?: {
        location: {
            lat: number;
            lng: number;
        };
    };
}

export interface ExtractedAddress {
    subpremise: string; // Unit/Apartment number (e.g., "711" from "711/3 Network Place")
    streetNumber: string;
    route: string;
    locality: string;
    administrativeAreaLevel1: string;
    administrativeAreaLevel1Long: string;
    administrativeAreaLevel2: string;
    administrativeAreaLevel2Long: string;
    country: string;
    postalCode: string;
    fullStreetAddress: string; // Combined: "711/3 Network Place" or "3 Network Place"
}

export interface FormattedAddress {
    address1: string;
    city: string;
    country: string;
    state: string;
    postal: string;
}

export interface UseGoogleAutocompleteOptions {
    apiKey: string;
    libraries?: string[];
    version?: string;
    language?: string;
    region?: string;
    /** When true, defer loading the Google Maps script until searchPlaces() is first called. Default: false (eager load). */
    defer?: boolean;
}

export interface UseGoogleAutocompleteResult {
    predictions: GooglePrediction[];
    isLoading: boolean;
    isScriptLoaded: boolean;
    searchPlaces: (input: string, countryRestriction?: string) => void;
    getPlaceDetails: (placeId: string) => Promise<GooglePlaceDetails | null>;
    extractAddressComponents: (place: GooglePlaceDetails) => ExtractedAddress;
    extractFormattedAddress: (place: GooglePlaceDetails) => FormattedAddress;
    clearPredictions: () => void;
}

/**
 * React hook for Google Places Autocomplete with automatic script injection
 * Automatically loads the Google Maps JavaScript API with Places library
 */
export function useGoogleAutocomplete(options: UseGoogleAutocompleteOptions): UseGoogleAutocompleteResult {
    const { apiKey, libraries = ['places'], version = 'weekly', language, region, defer = false } = options;

    const [predictions, setPredictions] = useState<GooglePrediction[]>([]);
    const [isLoading, setIsLoading] = useState(false);
    const [isScriptLoaded, setIsScriptLoaded] = useState(false);

    const autocompleteServiceRef = useRef<any>(null);
    const placesServiceRef = useRef<any>(null);
    const scriptLoadedRef = useRef(false);
    const debounceTimeoutRef = useRef<NodeJS.Timeout | null>(null);
    const pendingSearchRef = useRef<{ input: string; countryRestriction?: string } | null>(null);

    // Shared script injection logic (used by both eager useEffect and lazy trigger)
    const injectScript = useCallback(() => {
        if (scriptLoadedRef.current || window.google?.maps) {
            setIsScriptLoaded(true);
            scriptLoadedRef.current = true;
            return;
        }

        const existingScript = document.querySelector('script[src*="maps.googleapis.com"]');
        if (existingScript) {
            const checkLoaded = () => {
                if (window.google?.maps?.places?.AutocompleteService) {
                    setIsScriptLoaded(true);
                    scriptLoadedRef.current = true;
                } else {
                    setTimeout(checkLoaded, 100);
                }
            };
            checkLoaded();
            return;
        }

        const script = document.createElement('script');
        const params = new URLSearchParams({
            key: apiKey,
            libraries: libraries.join(','),
            v: version,
        });
        if (language) params.append('language', language);
        if (region) params.append('region', region);

        script.src = `https://maps.googleapis.com/maps/api/js?${params.toString()}`;
        script.async = true;
        script.defer = true;

        script.onload = () => {
            setIsScriptLoaded(true);
            scriptLoadedRef.current = true;
        };

        script.onerror = () => {
            // Failed to load Google Maps API
        };

        document.head.appendChild(script);
    }, [apiKey, libraries, version, language, region]);

    // Inject Google Maps script eagerly (unless defer is true)
    useEffect(() => {
        if (!apiKey || defer) {
            return;
        }
        injectScript();
    }, [apiKey, defer, injectScript]);

    // When script finishes loading in deferred mode, replay the pending search
    const searchPlacesRef = useRef<((input: string, countryRestriction?: string) => void) | null>(null);
    useEffect(() => {
        if (isScriptLoaded && pendingSearchRef.current && searchPlacesRef.current) {
            const { input, countryRestriction } = pendingSearchRef.current;
            pendingSearchRef.current = null;
            searchPlacesRef.current(input, countryRestriction);
        }
    }, [isScriptLoaded]);

    // Initialize Google Places services
    const initializeServices = useCallback(() => {
        if (typeof window === 'undefined') return false;

        if (!window.google?.maps?.places?.AutocompleteService) {
            return false;
        }

        if (!autocompleteServiceRef.current) {
            autocompleteServiceRef.current = new window.google.maps.places.AutocompleteService();
        }

        if (!placesServiceRef.current) {
            // Create a dummy map for PlacesService (required by Google API)
            const map = new window.google.maps.Map(document.createElement('div'));
            placesServiceRef.current = new window.google.maps.places.PlacesService(map);
        }

        return true;
    }, []);

    // Search for place predictions
    const searchPlaces = useCallback(
        (input: string, countryRestriction?: string) => {
            if (!isScriptLoaded) {
                // When deferred, trigger lazy load and queue the search for replay
                if (defer && !scriptLoadedRef.current) {
                    pendingSearchRef.current = { input, countryRestriction };
                    injectScript();
                }
                return;
            }

            if (!initializeServices()) {
                return;
            }

            if (input.length < 3) {
                setPredictions([]);
                return;
            }

            // Clear existing timeout
            if (debounceTimeoutRef.current) {
                clearTimeout(debounceTimeoutRef.current);
            }

            // Set new timeout
            debounceTimeoutRef.current = setTimeout(() => {
                setIsLoading(true);

                const request: any = {
                    input,
                    ...(countryRestriction && {
                        componentRestrictions: { country: countryRestriction.toLowerCase() },
                    }),
                };

                autocompleteServiceRef.current.getPlacePredictions(request, (results: any, status: any) => {
                    setIsLoading(false);

                    if (status === (window.google as any).maps.places.PlacesServiceStatus.OK && results) {
                        setPredictions(results as GooglePrediction[]);
                    } else {
                        setPredictions([]);
                        if (status !== (window.google as any).maps.places.PlacesServiceStatus.ZERO_RESULTS) {
                            // Prediction failed but not zero results
                        }
                    }
                });
            }, 300); // 300ms debounce
        },
        [initializeServices, isScriptLoaded, defer, injectScript],
    );

    // Keep ref in sync so the deferred-replay useEffect can call searchPlaces
    searchPlacesRef.current = searchPlaces;

    // Get detailed place information
    const getPlaceDetails = useCallback(
        (placeId: string): Promise<GooglePlaceDetails | null> => {
            return new Promise((resolve) => {
                if (!isScriptLoaded) {
                    resolve(null);
                    return;
                }

                if (!initializeServices()) {
                    resolve(null);
                    return;
                }

                const request = {
                    placeId,
                    fields: ['place_id', 'formatted_address', 'address_components', 'geometry'],
                };

                placesServiceRef.current.getDetails(request, (place: any, status: any) => {
                    if (status === (window.google as any).maps.places.PlacesServiceStatus.OK && place) {
                        resolve(place as GooglePlaceDetails);
                    } else {
                        resolve(null);
                    }
                });
            });
        },
        [initializeServices, isScriptLoaded],
    );

    // Extract structured address components from Google place
    const extractAddressComponents = useCallback((place: GooglePlaceDetails): ExtractedAddress => {
        const extracted: ExtractedAddress = {
            subpremise: '',
            streetNumber: '',
            route: '',
            locality: '',
            administrativeAreaLevel1: '',
            administrativeAreaLevel1Long: '',
            administrativeAreaLevel2: '',
            administrativeAreaLevel2Long: '',
            country: '',
            postalCode: '',
            fullStreetAddress: '',
        };


        // Temporary variables for comprehensive address component extraction
        let premise = '';
        let neighborhood = '';
        let sublocality5 = '';
        let sublocality4 = '';
        let sublocality3 = '';
        let sublocality2 = '';
        let sublocality1 = '';
        let postalTown = '';

        place.address_components?.forEach((component) => {
            const types = component.types;

            if (types.includes('subpremise')) {
                // Unit/Apartment number (e.g., "Unit 711", "711", "Apt 5B")
                extracted.subpremise = component.long_name;
            }
            if (types.includes('street_number')) {
                extracted.streetNumber = component.long_name;
            }
            if (types.includes('premise')) {
                // Building number (used in Asian addresses instead of street_number)
                premise = component.long_name;
            }
            if (types.includes('route')) {
                extracted.route = component.long_name;
            }
            if (types.includes('neighborhood')) {
                // Neighborhood (alternative to sublocality in some regions)
                neighborhood = component.long_name;
            }
            if (types.includes('sublocality_level_5')) {
                // Fifth-level sublocality (most granular)
                sublocality5 = component.long_name;
            }
            if (types.includes('sublocality_level_4')) {
                // Street name in Asian addresses (e.g., Korean: "Yulgok-ro 27-gil", Japanese block)
                sublocality4 = component.long_name;
            }
            if (types.includes('sublocality_level_3')) {
                // Neighborhood (e.g., Japanese chōme: "3-chōme")
                sublocality3 = component.long_name;
            }
            if (types.includes('sublocality_level_2')) {
                // Second-level sublocality (e.g., Japanese area: "Hirano")
                sublocality2 = component.long_name;
            }
            if (types.includes('sublocality_level_1')) {
                // District (fallback for city in some Asian countries)
                sublocality1 = component.long_name;
            }
            if (types.includes('locality')) {
                extracted.locality = component.long_name;
            }
            if (types.includes('postal_town')) {
                // Postal town (used in UK, Ireland instead of locality)
                postalTown = component.long_name;
            }
            if (types.includes('administrative_area_level_2')) {
                extracted.administrativeAreaLevel2 = component.short_name;
                extracted.administrativeAreaLevel2Long = component.long_name;
            }
            if (types.includes('administrative_area_level_1')) {
                extracted.administrativeAreaLevel1 = component.short_name;
                extracted.administrativeAreaLevel1Long = component.long_name;
            }
            if (types.includes('country')) {
                extracted.country = component.short_name;
            }
            if (types.includes('postal_code')) {
                extracted.postalCode = component.long_name;
            }
        });

        // === APPLY COMPREHENSIVE FALLBACK LOGIC ===

        // 1. Building/House Number with fallbacks
        // Priority: street_number (Western) > premise (Asian)
        const buildingNumber = extracted.streetNumber || premise;

        // 2. Street Name with comprehensive fallbacks
        // Priority: route (Western) > sublocality_level_5 > sublocality_level_4 (Asian streets)
        //          > sublocality_level_3 (Neighborhood) > neighborhood
        const streetName = extracted.route || sublocality5 || sublocality4 || sublocality3 || neighborhood;

        // 3. Update locality with fallbacks if not set
        // Priority: locality > postal_town > sublocality_level_2 > sublocality_level_1
        if (!extracted.locality) {
            extracted.locality = postalTown || sublocality2 || sublocality1 || '';
        }

        // 4. State fallback for French addresses
        // For countries like France where administrative_area_level_1 (région) may be missing,
        // use administrative_area_level_2 (département) as the primary state/province value
        if (!extracted.administrativeAreaLevel1 && extracted.administrativeAreaLevel2) {
            extracted.administrativeAreaLevel1 = extracted.administrativeAreaLevel2Long || extracted.administrativeAreaLevel2;
            extracted.administrativeAreaLevel1Long = extracted.administrativeAreaLevel2Long;
        }

        // === CONSTRUCT FULL STREET ADDRESS ===
        let addressValue = '';

        if (extracted.subpremise && buildingNumber && streetName) {
            // Unit + Building + Street (e.g., "711/3 Network Place", "5/3 Yulgok-ro 27-gil")
            addressValue = `${extracted.subpremise}/${buildingNumber} ${streetName}`;

        } else if (buildingNumber && streetName) {
            // Building + Street (e.g., "3 Network Place", "3-8 Hirano" for Japanese)
            // For Japanese addresses, check if we have multiple sublocality levels to combine
            if (premise && sublocality4 && sublocality3) {
                // Japanese format: "3-chōme-8-3" (chome-block-building)
                addressValue = `${sublocality3}-${sublocality4}-${premise}`;
                // Add area name if available (e.g., "3-chōme-8-3 Hirano")
                if (sublocality2) {
                    addressValue = `${addressValue} ${sublocality2}`;
                }
            } else {
                // Standard format
                addressValue = `${buildingNumber} ${streetName}`;
            }

        } else if (streetName) {
            // Only street name available (e.g., "Pyeongtaek 3-ro 56beon-gil")
            addressValue = streetName;

        } else if (buildingNumber) {
            // Edge case: Only building number (rare)
            addressValue = buildingNumber;
        }

        extracted.fullStreetAddress = addressValue.trim();

        return extracted;
    }, []);


    // Extract address in the format expected by shipping/billing address forms
    const extractFormattedAddress = useCallback((place: GooglePlaceDetails): FormattedAddress => {
        const extracted = extractAddressComponents(place);

        return {
            address1: extracted.fullStreetAddress || '',
            city: extracted.locality || '',
            country: extracted.country || '',
            state: extracted.administrativeAreaLevel1 || '',
            postal: extracted.postalCode || '',
        };
    }, [extractAddressComponents]);


    // Clear predictions
    const clearPredictions = useCallback(() => {
        setPredictions([]);
    }, []);

    return {
        predictions,
        isLoading,
        isScriptLoaded,
        searchPlaces,
        getPlaceDetails,
        extractAddressComponents,
        extractFormattedAddress,
        clearPredictions,
    };
}
